import { useEffect } from "react";

type DeleteKind = "folder" | "report" | "template";

interface DestructiveConfirmDialogProps {
    open: boolean;
    kind: DeleteKind;
    subject: string;
    context?: string;
    impact: string[];
    title?: string;
    description?: string;
    kicker?: string;
    confirmLabel?: string;
    loadingLabel?: string;
    confirmIcon?: string;
    confirmTone?: "danger" | "primary";
    secondaryActionLabel?: string;
    secondaryActionIcon?: string;
    secondaryActionDisabled?: boolean;
    onSecondaryAction?: () => void | Promise<void>;
    cancelLabel?: string;
    subjectIcon?: string;
    visualIcon?: string;
    loading?: boolean;
    onCancel: () => void;
    onConfirm: () => void | Promise<void>;
}

export function DestructiveConfirmDialog({
    open,
    kind,
    subject,
    context,
    impact,
    title,
    description,
    kicker,
    confirmLabel,
    loadingLabel,
    confirmIcon,
    confirmTone = "danger",
    secondaryActionLabel,
    secondaryActionIcon,
    secondaryActionDisabled = false,
    onSecondaryAction,
    cancelLabel,
    subjectIcon,
    visualIcon,
    loading = false,
    onCancel,
    onConfirm,
}: DestructiveConfirmDialogProps) {
    useEffect(() => {
        if (!open) return;

        const closeOnEscape = (event: KeyboardEvent) => {
            if (event.key === "Escape" && !loading) onCancel();
        };

        document.addEventListener("keydown", closeOnEscape);
        return () => document.removeEventListener("keydown", closeOnEscape);
    }, [loading, onCancel, open]);

    if (!open) return null;

    const resolvedVisualIcon = visualIcon || (kind === "folder" ? "folder_delete" : kind === "template" ? "auto_awesome_motion" : "delete");
    const resolvedTitle = title || (kind === "folder" ? "Delete Folder" : kind === "template" ? "Delete Template" : "Delete Report");
    const noun = kind === "folder" ? "folder" : kind === "template" ? "template" : "report";
    const resolvedSubjectIcon = subjectIcon || (kind === "folder" ? "folder" : kind === "template" ? "view_module" : "description");
    const resolvedDescription = description || (kind === "template"
        ? "Confirm before removing this reusable Excel layout template."
        : `Confirm before removing this ${noun} from the selected report plan.`);
    const resolvedConfirmLabel = confirmLabel || "Delete";
    const resolvedLoadingLabel = loadingLabel || (resolvedConfirmLabel === "Delete" ? "Deleting..." : "Working...");

    return (
        <div className="rc-confirm-layer" role="presentation" onMouseDown={() => !loading && onCancel()}>
            <section
                aria-labelledby="rc-delete-confirm-title"
                aria-modal="true"
                className="rc-confirm-panel"
                role="dialog"
                onMouseDown={(event) => event.stopPropagation()}
            >
                <div className="rc-confirm-visual" aria-hidden="true">
                    <span className="rc-confirm-orbit">
                        <span className="material-icons md-18">{resolvedVisualIcon}</span>
                    </span>
                </div>
                <div className="rc-confirm-copy">
                    <span className="rc-confirm-kicker">{kicker || "Permanent action"}</span>
                    <h3 id="rc-delete-confirm-title">{resolvedTitle}</h3>
                    <p>{resolvedDescription}</p>
                </div>
                <button className="rc-confirm-close" type="button" aria-label={cancelLabel || "Cancel delete"} disabled={loading} onClick={onCancel}>
                    <span className="material-icons md-18" aria-hidden="true">close</span>
                </button>

                <div className="rc-confirm-subject">
                    <span className="material-icons md-18" aria-hidden="true">{resolvedSubjectIcon}</span>
                    <div>
                        <strong>{subject}</strong>
                        {context && <small>{context}</small>}
                    </div>
                </div>

                <ul className="rc-confirm-impact">
                    {impact.map((item) => (
                        <li key={item}>
                            <span className="material-icons md-14" aria-hidden="true">priority_high</span>
                            <span>{item}</span>
                        </li>
                    ))}
                </ul>

                <div className="rc-confirm-actions">
                    <button className="rc-confirm-button rc-confirm-cancel" type="button" disabled={loading} onClick={onCancel}>
                        {cancelLabel || "Cancel"}
                    </button>
                    {secondaryActionLabel && onSecondaryAction && (
                        <button className="rc-confirm-button rc-confirm-secondary-danger" type="button" disabled={loading || secondaryActionDisabled} onClick={() => void onSecondaryAction()}>
                            <span className="material-icons md-16" aria-hidden="true">{secondaryActionIcon || "restart_alt"}</span>
                            <span>{secondaryActionLabel}</span>
                        </button>
                    )}
                    <button className={`rc-confirm-button ${confirmTone === "primary" ? "rc-confirm-primary" : "rc-confirm-danger"}`} type="button" disabled={loading} onClick={() => void onConfirm()}>
                        <span className="material-icons md-16" aria-hidden="true">{loading ? "sync" : (confirmIcon || "delete")}</span>
                        <span>{loading ? resolvedLoadingLabel : resolvedConfirmLabel}</span>
                    </button>
                </div>
            </section>
        </div>
    );
}
